<template>
  <div class="fixed z-10 inset-0 overflow-y-auto" id="contentPettyCashMovement">
    <div class="flex items-center py-8 justify-center min-h-screen">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-black opacity-50"></div>
      </div>
      <div class="bg-white rounded-lg overflow-hidden shadow-xl z-50 w-[96vw] max-w-4xl">
        <div class="flex justify-between items-center px-6 py-5 bg-blue-50 border-b border-blue-100">
          <div>
            <h2 class="text-xl font-bold text-blue-700 uppercase">Desembolso de caja chica</h2>
            <p class="text-sm text-blue-500 mt-1">
              Disponible para desembolsar: <strong>{{ money() }} {{ format(balance) }}</strong>
            </p>
          </div>
          <button
            @click="isActive"
            class="text-gray-500 hover:text-gray-700 w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-lg"
          >
            <svg class="w-5 h-5">
              <use href="../../assets/svg/icon.svg#cancel" />
            </svg>
          </button>
        </div>

        <form class="w-full space-y-6 px-6 py-6" @submit.prevent>
          <div class="grid gap-6">
            <label class="block">
              <span class="block mb-2 text-sm font-medium text-gray-800">
                Monto <span class="text-red-300">*</span>
              </span>
              <input
                type="number"
                min="0"
                class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                v-model="dataForm.amount"
                placeholder="0.00"
                @keydown.enter.prevent
              />
            </label>
            <label class="block">
              <span class="block mb-2 text-sm font-medium text-gray-800">
                Concepto <span class="text-red-300">*</span>
              </span>
              <textarea
                class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 resize-none w-full border-2 focus:border-blue-500"
                v-model="dataForm.concept"
                rows="3"
                maxlength="255"
              ></textarea>
            </label>
            <label class="block">
              <span class="block mb-2 text-sm font-medium text-gray-800">
                Beneficiario
              </span>
              <input
                type="text"
                class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                v-model="dataForm.beneficiary"
                maxlength="150"
                @keydown.enter.prevent
              />
            </label>
            <label class="block">
              <span class="block mb-2 text-sm font-medium text-gray-800">
                Departamento
              </span>
              <input
                type="text"
                class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                v-model="dataForm.departament"
                maxlength="150"
                @keydown.enter.prevent
              />
            </label>
          </div>

          <div class="flex justify-end">
            <button
              class="bg-blue-500 text-white px-4 py-2 font-semibold text-sm rounded-lg hover:shadow-sm hover:shadow-blue-600 active:scale-95 flex items-center"
              @click="insert"
              :disabled="spinner"
            >
              <div class="flex items-center" v-if="spinner">
                <svg class="w-4 h-4 mr-3 animate-spin">
                  <use href="../../assets/svg/icon.svg#spinnerForBlue" />
                </svg>
                Registrando
              </div>
              <span v-else>Registrar desembolso</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from "vue";
import axios from "@/api";
import { confirmationOfRecord, errorActions } from "@/alerts";
import { observeElement } from "@/observer";
import { idLocalStore, idLocalTurn, idLocalUser } from "@/publicjs/localStorage";
import { money, format } from "@/publicjs/money";

export default {
  name: "RegisterPettyCashMovement",
  props: {
    activeModal: {
      type: Function,
      required: true,
    },
    getdata: {
      type: Function,
      required: true,
    },
    balance: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const spinner = ref(false);
    const dataForm = ref({
      idStore: 0,
      idUser: 0,
      idTurn: 0,
      amount: "",
      beneficiary: "",
      departament: "",
      concept: "",
    });

    const isActive = () => {
      props.activeModal(true);
    };

    const insert = async () => {
      if (
        dataForm.value.amount === "" ||
        dataForm.value.amount === null ||
        parseFloat(dataForm.value.amount) <= 0
      ) {
        errorActions("Ingresa un monto mayor a 0 para el desembolso");
        return;
      }

      if (parseFloat(dataForm.value.amount) > parseFloat(props.balance)) {
        errorActions("El desembolso no puede ser mayor que el balance disponible");
        return;
      }

      if (dataForm.value.concept.trim().length <= 2) {
        errorActions("Ingresa el concepto del desembolso de caja chica");
        return;
      }

      spinner.value = true;
      const response = await axios
        .post(
          "caja-chica/movimientos",
          {
            idStore: dataForm.value.idStore,
            idUser: dataForm.value.idUser,
            idTurn: dataForm.value.idTurn,
            amount: parseFloat(dataForm.value.amount),
            beneficiary: dataForm.value.beneficiary,
            departament: dataForm.value.departament,
            concept: dataForm.value.concept,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        )
        .catch((error) => errorActions(error));

      if (!response) {
        spinner.value = false;
        return;
      }

      const data = await response.data;
      if (data.msg === true) {
        confirmationOfRecord(`El desembolso de caja chica con referencia <strong>${data.referenceNumber}</strong>`, {
          title: "Registrado",
        });
        dataForm.value.amount = "";
        dataForm.value.concept = "";
        dataForm.value.beneficiary = "";
        dataForm.value.departament = "";
        await props.getdata();
        props.activeModal(false);
      } else if (data.msg === "insufficient_balance") {
        errorActions("El monto supera el balance disponible en caja chica");
      } else if (data.msg === "not_found") {
        errorActions("No existe una caja chica abierta en esta sucursal");
      } else {
        errorActions("No se pudo registrar el desembolso de caja chica");
      }

      spinner.value = false;
    };

    onMounted(async () => {
      observeElement("#contentPettyCashMovement");
      dataForm.value.idStore = await idLocalStore();
      dataForm.value.idUser = await idLocalUser();
      dataForm.value.idTurn = await idLocalTurn();
    });

    return {
      dataForm,
      spinner,
      isActive,
      insert,
      money,
      format,
    };
  },
};
</script>